import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import _, { map } from 'lodash';
import { Mutation, Action } from 'vuex-module-decorators';
import { EdgeAccountApi, EdgeSiteAccountApi, PayorV4Api } from '@trialcard/apigateway.client';
import moment from 'moment';
import { Resolve } from 'vue-di';
import {
    ScriptsSiteAccountV2PatientsPatientAccountModel,
    PrescriberModel,
    PatientsSearchPatientsRequestModel,
    SiteModel,
    AccountModel,
} from '@trialcard/apigateway.models';
import { AccountExternalIdType, AccountRoleType, EnrollmentStatus, EnrollmentType } from '@trialcard/enums';
import AccountModule from './account';
import { TcPatientAccountDetailsModel, PatientAccountDisplayDetails, ClaimDisplayDetails } from './interfaces';
import SessionModule from './session';
import PracticeModule from './practice';
import { SearchFields } from '~/components/patient/searchFields';
import { PatientSearchUniqueIdenitfierType, SearchEnrollmentStatus } from '~/types/searchTypes';

@InjectModule({ stateFactory: true }, module)
// @OnClear<SearchModule>(async value => value.clear())
export default class SearchModule extends InjectVuexModule {
    @Resolve
    siteAccountApi!: EdgeSiteAccountApi;
    @Resolve
    practiceModule!: PracticeModule;
    @Resolve
    accountApi!: EdgeAccountApi;
    @Resolve
    accountModule!: AccountModule;
    @Resolve
    session!: SessionModule;
    @Resolve
    payorApi!: PayorV4Api;

    results: PatientAccountDisplayDetails[] = [];
    currentPatient!: PatientAccountDisplayDetails;
    currentEdit: any;
    currentDelete: any;
    hubSearchResultPrescribers: PrescriberModel[] = [];
    setCurrentEdit(item: any): void {
        this.currentEdit = item;
    }
    setCurrentDelete(item: any): void {
        this.currentDelete = item;
    }
    getCurrentEdit() {
        return this.currentEdit;
    }
    setCurrentPatient(patient: PatientAccountDisplayDetails): void {
        this.currentPatient = patient;
    }
    getCurrentPatient() {
        return this.currentPatient;
    }

    @Mutation
    setHubSearchResultPrescribers(data: PrescriberModel[]) {
        this.hubSearchResultPrescribers = data;
    }

    @Mutation
    clearResults(): void {
        this.hubSearchResultPrescribers = [];
        this.results = [];
    }

    @Action({})
    public async getSearchResultsByPatientDemographics(request: SearchFields) {
        this.clearResults();
        const apiRequest = {
            maxRecordCount: 5000,
            firstName: request.firstName || undefined,
            lastName: request.lastName || undefined,
            dateOfBirth: request.dateOfBirth ? moment(request.dateOfBirth) : undefined,
            state: request.state || undefined,
            prescriberId: request.prescriber || undefined,
            accountExternalIdType: this.session['https://apigateway.trialcard.com/account']
                ? AccountExternalIdType.SiteAccountId
                : undefined,
            accountExternalId: this.session['https://apigateway.trialcard.com/account']
                ? this.session['https://apigateway.trialcard.com/account'].toString()
                : undefined,
        };
        try {
            const searchResultsResponse = this.session.isHub
                ? await this.siteAccountApi.patientsSearchAllProgramPatients(apiRequest)
                : await this.siteAccountApi.patientsSearchPatients(apiRequest);
            const results = searchResultsResponse.data && searchResultsResponse.data.data;
            if (!results || results.length === 0) {
                return [];
            }
            const accountResults: PatientAccountDisplayDetails[] = [];
            if (this.session.isHub) {
                await this.setHubSearchResultAccountPrescribers(results);
            }
            let allPractices = [] as SiteModel[];
            if (!this.session.isHub) {
                allPractices = (await this.practiceModule.getPracticesAPI()) || [];
            } else {
                const siteIds = _.uniq(
                    results
                        .map(account => {
                            const site = account?.account?.sites?.find(site => site?.isPrimary);
                            return site?.relatedId || -1;
                        })
                        .filter(id => id > 0)
                );
                allPractices = (await this.practiceModule.getPracticesAPI(undefined, siteIds)) || [];
            }
            this.practiceModule.setPracticeModel(allPractices);
            await Promise.all(
                results.map(async (accts: ScriptsSiteAccountV2PatientsPatientAccountModel) => {
                    return this.getPatientAccountDetails(accts, allPractices).then(response => {
                        accountResults.push(response);
                        return;
                    });
                })
            );
            return accountResults;
        } catch {
            return [];
        }
    }

    @Action({})
    public async getSearchResultsByUniqueId(idSearch: { uniqueIdType: PatientSearchUniqueIdenitfierType; uniqueId: string }) {
        this.clearResults();

        try {
            let searchResultsResponse: any;
            const apiRequest: any = { maxRecordCount: 10 };
            switch (idSearch.uniqueIdType) {
                case PatientSearchUniqueIdenitfierType.MedicalMemberId: {
                    // eslint-disable-next-line no-unused-expressions
                    apiRequest.accountExternalIdType = this.getExternalIdType(idSearch.uniqueIdType);
                    apiRequest.accountExternalId = idSearch.uniqueId;
                    searchResultsResponse = await (this.session.isHub
                        ? this.siteAccountApi.patientsSearchAllProgramPatients(apiRequest)
                        : this.siteAccountApi.patientsSearchPatients(apiRequest));
                    break;
                }
                case PatientSearchUniqueIdenitfierType.PrescriberNpi: {
                    apiRequest.npi = idSearch.uniqueId;
                    searchResultsResponse = await this.siteAccountApi.patientsSearchPatientAccountsByPrescriber(apiRequest);
                    break;
                }
            }

            const results = searchResultsResponse.data && searchResultsResponse.data.data;
            if (!results || results.length === 0) {
                return [];
            }
            const accountResults: PatientAccountDisplayDetails[] = [];
            let allPractices: SiteModel[] = [];
            if (!this.session.isHub) {
                allPractices = (await this.practiceModule.getPracticesAPI()) || [];
            } else {
                await this.setHubSearchResultAccountPrescribers(results);
                const siteIds = _.uniq(
                    (results as ScriptsSiteAccountV2PatientsPatientAccountModel[])
                        .map(account => {
                            const site = account?.account?.sites?.find(site => site?.isPrimary);
                            return site?.relatedId || -1;
                        })
                        .filter(id => id > 0)
                );
                allPractices = (await this.practiceModule.getPracticesAPI(undefined, siteIds)) || [];
            }
            this.practiceModule.setPracticeModel(allPractices);
            await Promise.all(
                results.map(async (accts: ScriptsSiteAccountV2PatientsPatientAccountModel) => {
                    return this.getPatientAccountDetails(accts, allPractices).then(response => {
                        accountResults.push(response);
                        return;
                    });
                })
            );
            return accountResults;
        } catch {
            return [];
        }
    }

    private async getAccountEnrollment(patientAccount: ScriptsSiteAccountV2PatientsPatientAccountModel) {
        const enrollment = await this.accountModule.getEnrollment({
            ...patientAccount.patient,
            zip:
                patientAccount.patient.addresses && patientAccount.patient.addresses.length > 0 && patientAccount.patient.addresses[0].zip!,
        } as any);

        return { enrollment };
    }

    private async getPatientAccountDetails(
        patientAccount: ScriptsSiteAccountV2PatientsPatientAccountModel,
        siteModels: SiteModel[]
    ): Promise<PatientAccountDisplayDetails> {
        // Get Prescribers
        const accountPrescribers: PrescriberModel[] = [];
        if (this.session.isHub) {
            const accountPrescribersIds = patientAccount.account.prescribers.map(item => item.relatedId!);
            accountPrescribersIds.map(id => {
                const prescriberModel = this.hubSearchResultPrescribers.find(x => x.prescriberId === id);
                if (prescriberModel) accountPrescribers.push(prescriberModel);
            });
        } else {
            const sitePrescribersResponse = await this.accountModule.getSiteAccountPrescribers();
            const patientAccountPrescribers = patientAccount.account.prescribers;
            for (const pr of patientAccountPrescribers) {
                const prescriber = sitePrescribersResponse!.find(st => st.prescriberId === pr.relatedId);
                if (prescriber) accountPrescribers.push(prescriber);
            }
        }

        // Find the practice associated with this account
        let accountPractice = {} as SiteModel | undefined;
        const accountSiteIds = patientAccount.account?.sites?.find(site => site.isPrimary);
        accountPractice = siteModels.find(item => item.siteId === accountSiteIds?.relatedId);

        // Get Primary Medical Payor
        const medicalPayors =
            patientAccount.account.payors &&
            patientAccount.account.payors.length > 0 &&
            patientAccount.account.payors.find(py => py.roleType === AccountRoleType.InsuranceProvider);
        const primaryMedicalPayor = medicalPayors && medicalPayors.accountRelations.find(x => x.coverageOrder === 1);
        const accountPayors = [];
        const payorBenefit = [];
        if (primaryMedicalPayor) {
            const payorModel = await this.accountModule.getPatientAccountPayor(primaryMedicalPayor.relatedId!);
            const accountId = patientAccount.account.accountId;
            const payorId = primaryMedicalPayor.relatedId;
            if (payorModel && accountId && payorId) {
                accountPayors.push(payorModel);
                const payorBenefitModel = await this.payorApi.accountMedicalGet2(accountId as number, payorId as number);
                if (payorBenefitModel) payorBenefit.push(payorBenefitModel.data.data);
            }
        }
        // Get Enrollment and claims
        const enrollmentApi = await this.getAccountEnrollment(patientAccount).then(data => {
            return data;
        });

        const patientAccountDetails = {
            patientAccount,
            prescribers: accountPrescribers,
            payors: accountPayors,
            benefit: payorBenefit,
            enrollment: enrollmentApi.enrollment,
            practice: accountPractice,
        } as TcPatientAccountDetailsModel;

        return this.mapToPatientAccountDisplay(patientAccountDetails);
    }

    private mapToPatientAccountDisplay(patientAccountDetails: TcPatientAccountDetailsModel): PatientAccountDisplayDetails {
        const patient = patientAccountDetails.patientAccount.patient;
        const enrollment = patientAccountDetails.enrollment;
        const medicalMemberNumber =
            enrollment &&
            enrollment!.memberNumbers.find(x => {
                return x.type === EnrollmentType.Medical;
            });
        const pharmacyMemberNumber =
            enrollment &&
            enrollment!.memberNumbers.find(x => {
                return x.type === EnrollmentType.Pharmacy;
            });
        const address = patient.addresses && patient.addresses.length > 0 && patient.addresses[0] ? patient.addresses[0] : undefined;
        const payor =
            patientAccountDetails.payors && patientAccountDetails.payors.length > 0 && patientAccountDetails.payors[0]
                ? patientAccountDetails.payors[0]
                : undefined;
        const prescriber =
            patientAccountDetails.prescribers && patientAccountDetails.prescribers.length > 0 && patientAccountDetails.prescribers[0]
                ? patientAccountDetails.prescribers[0]
                : undefined;
        const payorBenefit =
            patientAccountDetails.benefit && patientAccountDetails.benefit.length > 0 && patientAccountDetails.benefit[0]
                ? patientAccountDetails.benefit[0]
                : undefined;
        return {
            patientId: patient.patientId,
            firstName: patient.firstName,
            lastName: patient.lastName,
            dateOfBirth: patient.dateOfBirth && moment(patient.dateOfBirth).format('L'),
            addresses: patient.addresses,
            addressOne: address && address.addressOne,
            addressDisplay: `${address && address.addressOne} ${address && address.city}, ${address && address.state} ${address &&
                address.zip}`,
            city: address && address.city,
            state: address && address.state,
            zipCode: address && address.zip,
            payer: payor && payor.payorName,
            policyName: payorBenefit?.policyName,
            payorPrimary: payor,
            gender: patient.gender ? this.getGender(patient.gender.valueOf()) : '',
            genderValue: patient.gender || 0,
            physicianFullName: prescriber && `${prescriber.firstName} ${prescriber.lastName}`,
            physicianFirstName: prescriber && prescriber.firstName,
            physicianLastName: prescriber && prescriber.lastName,
            physicianState: prescriber && ((prescriber.addresses || [])[0] || {}).state,
            physicianNPI: prescriber && prescriber.npi,
            accountId: patientAccountDetails.patientAccount.account.displayId,
            accountIdNumber: patientAccountDetails.patientAccount.account.accountId,
            medicalMemberId: medicalMemberNumber && medicalMemberNumber.number,
            pharmacyMemberId: pharmacyMemberNumber && pharmacyMemberNumber.number,
            prescribers: patientAccountDetails.prescribers,
            practice: patientAccountDetails.practice,
            sites: patientAccountDetails.patientAccount.account.sites,
            enrollmentEndDate: enrollment && enrollment.endDate && moment(enrollment.endDate).format('L'),
            enrollmentStartDate: enrollment && enrollment.startDate && moment(enrollment.startDate).format('L'),
            enrollment: enrollment || {},
            phoneNumbers: patient.phoneNumbers,
            emailAddresses: patient.emailAddresses,
            enrollmentStatus:
                (enrollment && enrollment.status && enrollment.status.status && EnrollmentStatus[enrollment.status.status!]) || '',
            enrollmentStatusDisplay:
                (enrollment && enrollment.status && enrollment.status.status && SearchEnrollmentStatus[enrollment.status.status!]) || '',
        } as PatientAccountDisplayDetails;
    }

    @Action({})
    private async setHubSearchResultAccountPrescribers(request: ScriptsSiteAccountV2PatientsPatientAccountModel[]) {
        const searchPrescriberIds: number[] = [];
        request.map(acct => {
            const prescriberIds = acct.account.prescribers.map(pr => {
                return pr.relatedId!;
            });
            searchPrescriberIds.push(...prescriberIds);
        });
        const prescribersApiResponse = await this.accountApi.prescribersHubGetPrescribers({ prescriberIds: searchPrescriberIds });
        const prescriberData = (prescribersApiResponse && prescribersApiResponse.data && prescribersApiResponse.data.data) || [];
        this.setHubSearchResultPrescribers(prescriberData);
    }

    getGender(gender: number) {
        let result;
        switch (gender) {
            case 0:
                result = 'Unknown';
                break;
            case 1:
                result = 'Male';
                break;
            case 2:
                result = 'Female';
                break;
            case 3:
                result = 'Indeterminate';
                break;
        }
        return result;
    }

    getExternalIdType(uniqueIdType: PatientSearchUniqueIdenitfierType) {
        let result;
        switch (uniqueIdType) {
            case PatientSearchUniqueIdenitfierType.MedicalMemberId:
                result = AccountExternalIdType.MedMemberID;
                break;
            case PatientSearchUniqueIdenitfierType.PharmacyMemberId:
                result = AccountExternalIdType.PharMemberID;
                break;
            case PatientSearchUniqueIdenitfierType.AccountNumber:
                result = AccountExternalIdType.TrialCardHubId;
                break;
        }
        return result;
    }
}
